// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Formulas & BOMs Page
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/Tabs';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Plus, Search, Edit, Trash2, Copy, Eye, Sliders, Layers, ArrowRightLeft, Activity } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';

interface Formula {
  _id: string;
  code: string;
  name: string;
  version: string;
  status: string;
  primaryProductId: string;
  primaryProductName: string;
  primaryProductQty: number;
  costingMethod?: string;
  description?: string;
  effectiveFrom?: string;
  inputs: any[];
  outputs: any[];
  operations: any[];
  parameters?: any[];
  jointCostMethod: string;
  byProductCreditPolicy: string;
}

interface Product {
  _id: string;
  code: string;
  name: string;
  type: string;
  uom: string;
}

export function BomsFormulasPage() {
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingFormula, setViewingFormula] = useState<Formula | null>(null);
  const [editingFormula, setEditingFormula] = useState<Formula | null>(null);
  const [formData, setFormData] = useState<any>({});
  const { get, post, put, del } = useApi();
  
  useEffect(() => {
    fetchFormulas();
    fetchProducts();
  }, [get]);
  
  async function fetchFormulas() {
    setLoading(true);
    try {
      const res = await get('/api/manufacturing/boms');
      setFormulas(res.formulas || []);
    } catch (error) {
      console.error('Failed to fetch formulas:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchProducts() {
    try {
      const res = await get('/api/manufacturing/products');
      setProducts(res.products || []);
    } catch (error) {
      console.error('Failed to fetch manufacturing products:', error);
    }
  }
  
  function handleOpenCreate() {
    setEditingFormula(null);
    const p0 = products[0] || { _id: '', code: 'CRUDE-ARAB-LT', name: 'Arabian Light Crude Oil', uom: 'MT' };
    const p1 = products[1] || { _id: '', code: 'DIESEL-10PPM', name: 'Ultra Low Sulfur Diesel (10PPM)', uom: 'kg' };
    const p4 = products[4] || { _id: '', code: 'LPG-MIX', name: 'Liquefied Petroleum Gas (LPG)', uom: 'kg' };

    setFormData({
      code: `FORM-${Date.now().toString().slice(-4)}`,
      name: '',
      version: '1.0',
      description: '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      status: 'draft',
      primaryProductId: p0._id || '',
      primaryProductName: `${p0.code} - ${p0.name}`,
      primaryProductQty: 1000,
      costingMethod: 'standard',
      inputs: [
        { lineNumber: 1, productId: p0._id || '', productName: p0.name, qty: 1000, unit: p0.uom || 'MT', standardCost: 50 }
      ],
      outputs: [
        { lineNumber: 1, productId: p1._id || '', productName: p1.name, type: 'co_product', expectedQty: 700, yieldPct: 70, salesPrice: 85, separableCost: 5 },
        { lineNumber: 2, productId: p4._id || '', productName: p4.name, type: 'by_product', expectedQty: 250, yieldPct: 25, salesPrice: 40, separableCost: 2 }
      ],
      operations: [
        { lineNumber: 1, operationCode: 'OP-10', operationName: 'Atmospheric Distillation', workCenterType: 'distillation', setupTimeStd: 30, runTimeStdPerUnit: 2, laborHoursStd: 1.5, machineHoursStd: 2.0, targetTemp: 350, targetPressure: 1.5 },
        { lineNumber: 2, operationCode: 'OP-20', operationName: 'Fractionation & Cooling', workCenterType: 'separator', setupTimeStd: 15, runTimeStdPerUnit: 1, laborHoursStd: 1.0, machineHoursStd: 1.5, targetTemp: 120, targetPressure: 1.0 }
      ],
      parameters: [
        { parameterCode: 'PARAM-01', parameterName: 'API Gravity', targetValue: 32.5, minValue: 30, maxValue: 35, unit: '°API', isCritical: true },
        { parameterCode: 'PARAM-02', parameterName: 'Sulfur Content', targetValue: 0.05, minValue: 0, maxValue: 0.1, unit: 'wt%', isCritical: true },
        { parameterCode: 'PARAM-03', parameterName: 'Reflux Ratio', targetValue: 3.5, minValue: 3.0, maxValue: 4.0, unit: 'ratio', isCritical: false }
      ],
      jointCostMethod: 'nrv',
      byProductCreditPolicy: 'deduct_nrv',
    });
    setShowDialog(true);
  }

  function handleOpenView(formula: Formula) {
    setViewingFormula(formula);
    setShowViewModal(true);
  }
  
  function handleEdit(formula: Formula) {
    setEditingFormula(formula);
    setFormData({ ...formula });
    setShowDialog(true);
  }
  
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingFormula) {
        await put(`/api/manufacturing/boms/${editingFormula._id}`, formData);
      } else {
        await post('/api/manufacturing/boms', formData);
      }
      setShowDialog(false);
      fetchFormulas();
    } catch (error: any) {
      console.error('Failed to save formula:', error);
      const errMsg = error?.response?.data?.error || error?.message || 'Failed to save formula';
      alert(`Error saving formula: ${errMsg}`);
    }
  }
  
  async function handleDuplicate(formula: Formula) {
    const { _id, ...rest } = formula;
    const newFormula = {
      ...rest,
      code: `${formula.code}-V${Date.now().toString().slice(-4)}`,
      version: (parseFloat(formula.version || '1.0') + 0.1).toFixed(1),
      status: 'draft',
    };
    try {
      await post('/api/manufacturing/boms', newFormula);
      fetchFormulas();
    } catch (error) {
      console.error('Failed to duplicate formula:', error);
    }
  }
  
  async function handleDelete(id: string) {
    if (confirm('Are you sure you want to delete this formula?')) {
      await del(`/api/manufacturing/boms/${id}`);
      fetchFormulas();
    }
  }
  
  const filteredFormulas = formulas.filter(f => 
    (f.code || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.name || '').toLowerCase().includes(search.toLowerCase())
  ).filter(f => !filterStatus || f.status === filterStatus);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Formulas & BOMs</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Define multi-output split %, co-product/by-product ratios, process routings, and custom quality & operating parameters
          </p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Formula
        </Button>
      </div>
      
      <div className="flex gap-4 mb-4">
        <Input placeholder="Search formulas..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
        <div className="w-44">
          <Select 
            value={filterStatus} 
            onValueChange={setFilterStatus}
            placeholder="All Status"
            options={[
              { label: 'All Status', value: '' },
              { label: 'Draft', value: 'draft' },
              { label: 'Active', value: 'active' },
              { label: 'Archived', value: 'archived' },
              { label: 'Superseded', value: 'superseded' },
            ]}
          />
        </div>
      </div>
      
      {loading ? (
        <Card className="p-8 text-center">Loading formulas...</Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Formula Name</TableHead>
                <TableHead>Ver.</TableHead>
                <TableHead>Primary Product</TableHead>
                <TableHead>Inputs</TableHead>
                <TableHead>Outputs</TableHead>
                <TableHead>Operations</TableHead>
                <TableHead>Custom Params</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-48">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFormulas.map(formula => (
                <TableRow key={formula._id}>
                  <TableCell className="font-mono font-medium">{formula.code}</TableCell>
                  <TableCell className="font-medium">{formula.name}</TableCell>
                  <TableCell>{formula.version}</TableCell>
                  <TableCell>{formula.primaryProductName || formula.primaryProductId}</TableCell>
                  <TableCell><Badge variant="secondary" className="gap-1"><Layers className="w-3 h-3"/> {formula.inputs?.length || 0} Materials</Badge></TableCell>
                  <TableCell><Badge variant="outline" className="gap-1"><ArrowRightLeft className="w-3 h-3"/> {formula.outputs?.length || 0} Co-products</Badge></TableCell>
                  <TableCell><Badge variant="secondary" className="gap-1"><Activity className="w-3 h-3"/> {formula.operations?.length || 0} Stages</Badge></TableCell>
                  <TableCell><Badge variant="outline" className="gap-1"><Sliders className="w-3 h-3"/> {formula.parameters?.length || 0} Params</Badge></TableCell>
                  <TableCell><Badge variant={formula.status === 'active' ? 'success' : formula.status === 'draft' ? 'secondary' : 'outline'}>{formula.status}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleOpenView(formula)} title="View Formula Structure & Parameters">
                        <Eye className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(formula)} title="Edit">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDuplicate(formula)} title="Duplicate">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(formula._id)} title="Delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredFormulas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-gray-500">No formulas found. Click "Create Formula" to get started.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* View Details Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              Formula Structure: {viewingFormula?.code} — {viewingFormula?.name}
              <Badge variant="success">{viewingFormula?.status}</Badge>
            </DialogTitle>
          </DialogHeader>

          {viewingFormula && (
            <div className="space-y-6 p-2">
              <div className="grid grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div>
                  <span className="text-xs text-gray-500 block">Primary Output</span>
                  <span className="font-semibold">{viewingFormula.primaryProductName}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Batch Size</span>
                  <span className="font-semibold">{viewingFormula.primaryProductQty} kg</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">Allocation Method</span>
                  <span className="font-semibold">{viewingFormula.jointCostMethod?.toUpperCase()}</span>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-body-md mb-2 flex items-center gap-2"><Layers className="w-4 h-4 text-blue-500"/> Raw Material Inputs ({viewingFormula.inputs?.length || 0})</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Line</TableHead>
                      <TableHead>Raw Material Product</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Std Cost (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewingFormula.inputs?.map((inp: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>#{inp.lineNumber}</TableCell>
                        <TableCell className="font-medium">{inp.productName}</TableCell>
                        <TableCell>{inp.qty}</TableCell>
                        <TableCell>{inp.unit}</TableCell>
                        <TableCell>₹{inp.standardCost}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <h3 className="font-semibold text-body-md mb-2 flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-green-500"/> Output Products & Co-Products ({viewingFormula.outputs?.length || 0})</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Line</TableHead>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Expected Qty</TableHead>
                      <TableHead>Yield %</TableHead>
                      <TableHead>Sales Price (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewingFormula.outputs?.map((out: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>#{out.lineNumber}</TableCell>
                        <TableCell className="font-medium">{out.productName}</TableCell>
                        <TableCell><Badge variant="outline">{out.type}</Badge></TableCell>
                        <TableCell>{out.expectedQty}</TableCell>
                        <TableCell>{out.yieldPct}%</TableCell>
                        <TableCell>₹{out.salesPrice}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <h3 className="font-semibold text-body-md mb-2 flex items-center gap-2"><Activity className="w-4 h-4 text-purple-500"/> Routing Operations ({viewingFormula.operations?.length || 0})</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Operation Name</TableHead>
                      <TableHead>Work Center</TableHead>
                      <TableHead>Setup / Run</TableHead>
                      <TableHead>Labor / Machine</TableHead>
                      <TableHead>Temp / Pressure</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewingFormula.operations?.map((op: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono">{op.operationCode}</TableCell>
                        <TableCell className="font-medium">{op.operationName}</TableCell>
                        <TableCell><Badge variant="secondary">{op.workCenterType}</Badge></TableCell>
                        <TableCell>{op.setupTimeStd}m / {op.runTimeStdPerUnit}m</TableCell>
                        <TableCell>{op.laborHoursStd}h / {op.machineHoursStd}h</TableCell>
                        <TableCell>{op.targetTemp ? `${op.targetTemp}°C` : '-'} / {op.targetPressure ? `${op.targetPressure} bar` : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <h3 className="font-semibold text-body-md mb-2 flex items-center gap-2"><Sliders className="w-4 h-4 text-orange-500"/> Custom Operating & Quality Parameters ({viewingFormula.parameters?.length || 0})</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Parameter Name</TableHead>
                      <TableHead>Target Value</TableHead>
                      <TableHead>Min Value</TableHead>
                      <TableHead>Max Value</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Critical Quality</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewingFormula.parameters?.map((param: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono">{param.parameterCode}</TableCell>
                        <TableCell className="font-medium">{param.parameterName}</TableCell>
                        <TableCell className="font-bold">{param.targetValue ?? '-'}</TableCell>
                        <TableCell>{param.minValue ?? '-'}</TableCell>
                        <TableCell>{param.maxValue ?? '-'}</TableCell>
                        <TableCell>{param.unit}</TableCell>
                        <TableCell>{param.isCritical ? <Badge variant="destructive">Critical QC</Badge> : <Badge variant="secondary">Standard</Badge>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowViewModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFormula ? 'Edit Formula' : 'Create Formula'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="p-4 space-y-4">
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="inputs">Inputs ({formData.inputs?.length || 0})</TabsTrigger>
                  <TabsTrigger value="outputs">Outputs ({formData.outputs?.length || 0})</TabsTrigger>
                  <TabsTrigger value="operations">Operations ({formData.operations?.length || 0})</TabsTrigger>
                  <TabsTrigger value="parameters">Parameters ({formData.parameters?.length || 0})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="general" className="space-y-4 p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Formula Code *</label>
                      <Input value={formData.code || ''} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Formula Name *</label>
                      <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} required placeholder="e.g. Crude Distillation Run" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Version *</label>
                      <Input value={formData.version || '1.0'} onChange={e => setFormData({...formData, version: e.target.value})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Effective From *</label>
                      <Input type="date" value={formData.effectiveFrom || ''} onChange={e => setFormData({...formData, effectiveFrom: e.target.value})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Status *</label>
                      <Select 
                        value={formData.status || 'draft'} 
                        onValueChange={v => setFormData({...formData, status: v})}
                        options={[
                          { label: 'Draft', value: 'draft' },
                          { label: 'Active', value: 'active' },
                          { label: 'Archived', value: 'archived' },
                          { label: 'Superseded', value: 'superseded' }
                        ]}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Input value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="e.g. Standard distillation run parameters for Light Crude" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Primary Product *</label>
                      <Select 
                        value={formData.primaryProductId || ''} 
                        onValueChange={v => {
                          const p = products.find(prod => prod._id === v);
                          setFormData({
                            ...formData,
                            primaryProductId: v,
                            primaryProductName: p ? `${p.code} - ${p.name}` : formData.primaryProductName
                          });
                        }}
                        options={products.map(p => ({ label: `${p.code} - ${p.name} (${p.type})`, value: p._id }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Batch Size / Qty *</label>
                      <Input type="number" value={formData.primaryProductQty || 0} onChange={e => setFormData({...formData, primaryProductQty: Number(e.target.value)})} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Costing Method</label>
                      <Select 
                        value={formData.costingMethod || 'standard'} 
                        onValueChange={v => setFormData({...formData, costingMethod: v})}
                        options={[
                          { label: 'Standard', value: 'standard' },
                          { label: 'Actual', value: 'actual' },
                          { label: 'Hybrid', value: 'hybrid' }
                        ]}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Joint Cost Allocation Method</label>
                      <Select 
                        value={formData.jointCostMethod || 'nrv'} 
                        onValueChange={v => setFormData({...formData, jointCostMethod: v})}
                        options={[
                          { label: 'Net Realizable Value (NRV)', value: 'nrv' },
                          { label: 'Sales Value at Split-Off', value: 'sales_value_split_off' },
                          { label: 'Physical Quantity', value: 'physical_quantity' },
                          { label: 'Weighted Factor', value: 'weighted_factor' },
                          { label: 'Manual Percentage', value: 'manual' }
                        ]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">By-Product Credit Policy</label>
                      <Select 
                        value={formData.byProductCreditPolicy || 'deduct_nrv'} 
                        onValueChange={v => setFormData({...formData, byProductCreditPolicy: v})}
                        options={[
                          { label: 'Deduct NRV', value: 'deduct_nrv' },
                          { label: 'Deduct Net Salvage', value: 'deduct_net_salvage' },
                          { label: 'No Credit', value: 'no_credit' }
                        ]}
                      />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="inputs" className="p-4">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium">Raw Material Inputs</label>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const nextId = (formData.inputs?.length || 0) + 1;
                        const defaultProd = products[0] || { _id: '', name: 'Raw Material', uom: 'kg' };
                        setFormData({
                          ...formData,
                          inputs: [
                            ...(formData.inputs || []),
                            { lineNumber: nextId, productId: defaultProd._id || '', productName: defaultProd.name || 'Raw Material Input', qty: 100, unit: defaultProd.uom || 'kg', standardCost: 50 }
                          ]
                        });
                      }}>
                        <Plus className="w-4 h-4 mr-1" /> Add Input
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {formData.inputs?.map((input: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded flex-wrap">
                          <div className="w-14">
                            <label className="block text-xs text-gray-500 mb-1">Line #</label>
                            <Input type="number" value={input.lineNumber} onChange={e => { const newInputs = [...formData.inputs]; newInputs[idx] = {...newInputs[idx], lineNumber: Number(e.target.value)}; setFormData({...formData, inputs: newInputs}); }} />
                          </div>
                          <div className="w-52">
                            <label className="block text-xs text-gray-500 mb-1">Select Product</label>
                            <Select 
                              value={input.productId || ''} 
                              onValueChange={v => {
                                const p = products.find(prod => prod._id === v);
                                const newInputs = [...formData.inputs];
                                newInputs[idx] = {
                                  ...newInputs[idx],
                                  productId: v,
                                  productName: p?.name || newInputs[idx].productName,
                                  unit: p?.uom || newInputs[idx].unit
                                };
                                setFormData({...formData, inputs: newInputs});
                              }}
                              options={products.map(p => ({ label: `${p.code} - ${p.name}`, value: p._id }))}
                            />
                          </div>
                          <div className="w-48">
                            <label className="block text-xs text-gray-500 mb-1">Product Name *</label>
                            <Input 
                              value={input.productName || ''} 
                              onChange={e => {
                                const newInputs = [...formData.inputs];
                                newInputs[idx] = {...newInputs[idx], productName: e.target.value};
                                setFormData({...formData, inputs: newInputs});
                              }} 
                              placeholder="e.g. Arabian Light Crude"
                            />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Qty *</label>
                            <Input type="number" value={input.qty} onChange={e => { const newInputs = [...formData.inputs]; newInputs[idx] = {...newInputs[idx], qty: Number(e.target.value)}; setFormData({...formData, inputs: newInputs}); }} />
                          </div>
                          <div className="w-20">
                            <label className="block text-xs text-gray-500 mb-1">Unit</label>
                            <Input value={input.unit || 'kg'} onChange={e => { const newInputs = [...formData.inputs]; newInputs[idx] = {...newInputs[idx], unit: e.target.value}; setFormData({...formData, inputs: newInputs}); }} />
                          </div>
                          <div className="w-28">
                            <label className="block text-xs text-gray-500 mb-1">Std Cost (₹)</label>
                            <Input type="number" value={input.standardCost} onChange={e => { const newInputs = [...formData.inputs]; newInputs[idx] = {...newInputs[idx], standardCost: Number(e.target.value)}; setFormData({...formData, inputs: newInputs}); }} />
                          </div>
                          <div className="pt-5">
                            <Button type="button" variant="ghost" size="icon" onClick={() => { const newInputs = [...formData.inputs]; newInputs.splice(idx, 1); setFormData({...formData, inputs: newInputs}); }}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="outputs" className="p-4">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium">Output Products (Co-products, By-products, Waste)</label>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const nextId = (formData.outputs?.length || 0) + 1;
                        const defaultProd = products[1] || products[0] || { _id: '', name: 'Co-Product Output' };
                        setFormData({
                          ...formData,
                          outputs: [
                            ...(formData.outputs || []),
                            { lineNumber: nextId, productId: defaultProd._id || '', productName: defaultProd.name || 'Output Product', type: 'co_product', expectedQty: 100, yieldPct: 10, salesPrice: 60, separableCost: 0 }
                          ]
                        });
                      }}>
                        <Plus className="w-4 h-4 mr-1" /> Add Output
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {formData.outputs?.map((output: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded flex-wrap">
                          <div className="w-14">
                            <label className="block text-xs text-gray-500 mb-1">Line #</label>
                            <Input type="number" value={output.lineNumber} onChange={e => { const newOutputs = [...formData.outputs]; newOutputs[idx] = {...newOutputs[idx], lineNumber: Number(e.target.value)}; setFormData({...formData, outputs: newOutputs}); }} />
                          </div>
                          <div className="w-48">
                            <label className="block text-xs text-gray-500 mb-1">Select Product</label>
                            <Select 
                              value={output.productId || ''} 
                              onValueChange={v => {
                                const p = products.find(prod => prod._id === v);
                                const newOutputs = [...formData.outputs];
                                newOutputs[idx] = {
                                  ...newOutputs[idx],
                                  productId: v,
                                  productName: p?.name || newOutputs[idx].productName
                                };
                                setFormData({...formData, outputs: newOutputs});
                              }}
                              options={products.map(p => ({ label: `${p.code} - ${p.name}`, value: p._id }))}
                            />
                          </div>
                          <div className="w-44">
                            <label className="block text-xs text-gray-500 mb-1">Product Name *</label>
                            <Input 
                              value={output.productName || ''} 
                              onChange={e => {
                                const newOutputs = [...formData.outputs];
                                newOutputs[idx] = {...newOutputs[idx], productName: e.target.value};
                                setFormData({...formData, outputs: newOutputs});
                              }} 
                              placeholder="e.g. Diesel 10PPM"
                            />
                          </div>
                          <div className="w-36">
                            <label className="block text-xs text-gray-500 mb-1">Output Type *</label>
                            <Select 
                              value={output.type || 'co_product'} 
                              onValueChange={v => { const newOutputs = [...formData.outputs]; newOutputs[idx] = {...newOutputs[idx], type: v}; setFormData({...formData, outputs: newOutputs}); }}
                              options={[
                                { label: 'Co-Product', value: 'co_product' },
                                { label: 'By-Product', value: 'by_product' },
                                { label: 'Waste', value: 'waste' },
                                { label: 'Intermediate', value: 'intermediate' }
                              ]}
                            />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Expected Qty</label>
                            <Input type="number" value={output.expectedQty} onChange={e => { const newOutputs = [...formData.outputs]; newOutputs[idx] = {...newOutputs[idx], expectedQty: Number(e.target.value)}; setFormData({...formData, outputs: newOutputs}); }} />
                          </div>
                          <div className="w-20">
                            <label className="block text-xs text-gray-500 mb-1">Yield %</label>
                            <Input type="number" value={output.yieldPct} onChange={e => { const newOutputs = [...formData.outputs]; newOutputs[idx] = {...newOutputs[idx], yieldPct: Number(e.target.value)}; setFormData({...formData, outputs: newOutputs}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Sales Price (₹)</label>
                            <Input type="number" value={output.salesPrice} onChange={e => { const newOutputs = [...formData.outputs]; newOutputs[idx] = {...newOutputs[idx], salesPrice: Number(e.target.value)}; setFormData({...formData, outputs: newOutputs}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Sep. Cost (₹)</label>
                            <Input type="number" value={output.separableCost} onChange={e => { const newOutputs = [...formData.outputs]; newOutputs[idx] = {...newOutputs[idx], separableCost: Number(e.target.value)}; setFormData({...formData, outputs: newOutputs}); }} />
                          </div>
                          <div className="pt-5">
                            <Button type="button" variant="ghost" size="icon" onClick={() => { const newOutputs = [...formData.outputs]; newOutputs.splice(idx, 1); setFormData({...formData, outputs: newOutputs}); }}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="operations" className="p-4">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium">Routing Operations & Process Stages</label>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const nextId = (formData.operations?.length || 0) + 1;
                        setFormData({
                          ...formData,
                          operations: [
                            ...(formData.operations || []),
                            { lineNumber: nextId, operationCode: `OP-${nextId * 10}`, operationName: 'Process Run', workCenterType: 'distillation', setupTimeStd: 15, runTimeStdPerUnit: 1, laborHoursStd: 1, machineHoursStd: 1, targetTemp: 200, targetPressure: 1 }
                          ]
                        });
                      }}>
                        <Plus className="w-4 h-4 mr-1" /> Add Operation
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {formData.operations?.map((op: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded flex-wrap">
                          <div className="w-14">
                            <label className="block text-xs text-gray-500 mb-1">Line #</label>
                            <Input type="number" value={op.lineNumber} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], lineNumber: Number(e.target.value)}; setFormData({...formData, operations: newOps}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Op Code</label>
                            <Input value={op.operationCode} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], operationCode: e.target.value.toUpperCase()}; setFormData({...formData, operations: newOps}); }} />
                          </div>
                          <div className="w-44">
                            <label className="block text-xs text-gray-500 mb-1">Operation Name *</label>
                            <Input value={op.operationName} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], operationName: e.target.value}; setFormData({...formData, operations: newOps}); }} />
                          </div>
                          <div className="w-40">
                            <label className="block text-xs text-gray-500 mb-1">Work Center</label>
                            <Select 
                              value={op.workCenterType || 'distillation'} 
                              onValueChange={v => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], workCenterType: v}; setFormData({...formData, operations: newOps}); }}
                              options={[
                                { label: 'Distillation Unit', value: 'distillation' },
                                { label: 'Heat Exchanger', value: 'heat_exchanger' },
                                { label: 'Reactor Vessel', value: 'reactor' },
                                { label: 'Separator', value: 'separator' },
                                { label: 'Pump Station', value: 'pump' },
                                { label: 'Compressor', value: 'compressor' },
                                { label: 'Packaging Line', value: 'packaging' }
                              ]}
                            />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Setup (min)</label>
                            <Input type="number" value={op.setupTimeStd} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], setupTimeStd: Number(e.target.value)}; setFormData({...formData, operations: newOps}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Run/unit (m)</label>
                            <Input type="number" value={op.runTimeStdPerUnit} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], runTimeStdPerUnit: Number(e.target.value)}; setFormData({...formData, operations: newOps}); }} />
                          </div>
                          <div className="w-20">
                            <label className="block text-xs text-gray-500 mb-1">Labor (h)</label>
                            <Input type="number" value={op.laborHoursStd} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], laborHoursStd: Number(e.target.value)}; setFormData({...formData, operations: newOps}); }} />
                          </div>
                          <div className="w-20">
                            <label className="block text-xs text-gray-500 mb-1">Mach (h)</label>
                            <Input type="number" value={op.machineHoursStd} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], machineHoursStd: Number(e.target.value)}; setFormData({...formData, operations: newOps}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Temp (°C)</label>
                            <Input type="number" value={op.targetTemp || ''} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], targetTemp: Number(e.target.value)}; setFormData({...formData, operations: newOps}); }} placeholder="e.g. 350" />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Pressure (bar)</label>
                            <Input type="number" value={op.targetPressure || ''} onChange={e => { const newOps = [...formData.operations]; newOps[idx] = {...newOps[idx], targetPressure: Number(e.target.value)}; setFormData({...formData, operations: newOps}); }} placeholder="e.g. 1.5" />
                          </div>
                          <div className="pt-5">
                            <Button type="button" variant="ghost" size="icon" onClick={() => { const newOps = [...formData.operations]; newOps.splice(idx, 1); setFormData({...formData, operations: newOps}); }}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="parameters" className="p-4">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-sm font-medium block">Custom Operating & Quality Parameters</label>
                        <span className="text-xs text-gray-500">Add custom parameters like Reflux Ratio, Flow Rate, API Gravity, Sulfur Wt %, Flash Point, etc.</span>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const nextId = (formData.parameters?.length || 0) + 1;
                        setFormData({
                          ...formData,
                          parameters: [
                            ...(formData.parameters || []),
                            { parameterCode: `PARAM-0${nextId}`, parameterName: 'Custom Parameter', targetValue: 50, minValue: 0, maxValue: 100, unit: 'unit', isCritical: false }
                          ]
                        });
                      }}>
                        <Plus className="w-4 h-4 mr-1" /> Add Parameter
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {formData.parameters?.map((param: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded flex-wrap">
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Param Code</label>
                            <Input value={param.parameterCode || ''} onChange={e => { const newParams = [...formData.parameters]; newParams[idx] = {...newParams[idx], parameterCode: e.target.value.toUpperCase()}; setFormData({...formData, parameters: newParams}); }} />
                          </div>
                          <div className="w-52">
                            <label className="block text-xs text-gray-500 mb-1">Parameter Name *</label>
                            <Input value={param.parameterName || ''} onChange={e => { const newParams = [...formData.parameters]; newParams[idx] = {...newParams[idx], parameterName: e.target.value}; setFormData({...formData, parameters: newParams}); }} placeholder="e.g. Reflux Ratio" />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Target Val</label>
                            <Input type="number" value={param.targetValue ?? ''} onChange={e => { const newParams = [...formData.parameters]; newParams[idx] = {...newParams[idx], targetValue: e.target.value !== '' ? Number(e.target.value) : ''}; setFormData({...formData, parameters: newParams}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Min Limit</label>
                            <Input type="number" value={param.minValue ?? ''} onChange={e => { const newParams = [...formData.parameters]; newParams[idx] = {...newParams[idx], minValue: e.target.value !== '' ? Number(e.target.value) : ''}; setFormData({...formData, parameters: newParams}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Max Limit</label>
                            <Input type="number" value={param.maxValue ?? ''} onChange={e => { const newParams = [...formData.parameters]; newParams[idx] = {...newParams[idx], maxValue: e.target.value !== '' ? Number(e.target.value) : ''}; setFormData({...formData, parameters: newParams}); }} />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-gray-500 mb-1">Unit</label>
                            <Input value={param.unit || ''} onChange={e => { const newParams = [...formData.parameters]; newParams[idx] = {...newParams[idx], unit: e.target.value}; setFormData({...formData, parameters: newParams}); }} placeholder="e.g. °API" />
                          </div>
                          <div className="flex items-center gap-2 pt-5">
                            <input 
                              type="checkbox" 
                              id={`crit-${idx}`} 
                              checked={Boolean(param.isCritical)} 
                              onChange={e => { const newParams = [...formData.parameters]; newParams[idx] = {...newParams[idx], isCritical: e.target.checked}; setFormData({...formData, parameters: newParams}); }} 
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <label htmlFor={`crit-${idx}`} className="text-xs font-medium cursor-pointer">Critical QC</label>
                          </div>
                          <div className="pt-5">
                            <Button type="button" variant="ghost" size="icon" onClick={() => { const newParams = [...formData.parameters]; newParams.splice(idx, 1); setFormData({...formData, parameters: newParams}); }}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">{editingFormula ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}